import type { FileCollection } from '../../../utils/validationForm';
import { getBoneMarrowFileRoles } from './boneMarrowDocuments';

export interface IEconomicDocument {
  file: File;
  description: string;
  role: string;
}

export interface IExemptionTypeOption {
  id: string;
  name: string;
}

export const normalizeExemptionName = (name?: string) =>
  (name ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('pt-BR')
    .trim();

export const getExemptionType = (value?: string, options: IExemptionTypeOption[] = []) => {
  if (!value) return undefined;
  try {
    const parsed = JSON.parse(value) as IExemptionTypeOption;
    if (parsed?.id && parsed?.name) return parsed;
  } catch {
    // Mobile stores only the id; desktop stores the serialized option.
  }
  return options.find((option) => option.id === value);
};

export const isEconomicExemption = (name?: string) => normalizeExemptionName(name).includes('hipossuficiencia economica');

export const isCadunicoExemption = (name?: string) => normalizeExemptionName(name).includes('cadunico');

export const isBoneMarrowExemption = (name?: string) => normalizeExemptionName(name).includes('medula');

export const getExemptionFileRoles = (exemptionName: string, files?: FileCollection) => {
  if (!files?.length) return [];
  if (isBoneMarrowExemption(exemptionName)) return getBoneMarrowFileRoles(files);
  return Array.from(files).map(() => exemptionName);
};

interface IExemptionAttachmentValidation {
  exemptionName?: string;
  fileCount: number;
  requiresCadunicoDocument: boolean;
  hasActiveExistingAttachment?: boolean;
}

export const getExemptionAttachmentError = ({
  exemptionName,
  fileCount,
  requiresCadunicoDocument,
  hasActiveExistingAttachment = false,
}: IExemptionAttachmentValidation): string | undefined => {
  if (isEconomicExemption(exemptionName)) return undefined;
  if (isBoneMarrowExemption(exemptionName) && fileCount !== 3) {
    return 'Preencha os três campos de documentos obrigatórios do doador de medula.';
  }
  if (isCadunicoExemption(exemptionName) && requiresCadunicoDocument && fileCount === 0 && !hasActiveExistingAttachment) {
    return 'Anexe o comprovante de cadastro e regularidade do CadÚnico.';
  }
  return undefined;
};

export const appendEconomicDocuments = (form: FormData, documents: IEconomicDocument[], fileKey = 'files') => {
  documents.forEach(({ file, description, role }) => {
    form.append(fileKey, file);
    form.append('file_descriptions', description.trim());
    form.append('file_roles', role.trim());
  });
};
